import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { useRealtime } from "@/lib/useRealtime";
import {
  getAnfrage,
  getAnfrageMailCandidateCounts,
  listAnfragen,
  notifyAnfragenMailCandidatesChanged,
  type AnfrageDetail,
  type AnfrageDraftOut,
  type AnfrageListItem,
} from "@/lib/anfragen";
import { AnfragenListSection, type AnfragenView } from "@/pages/anfragen/AnfragenListSection";
import { AnfrageDetailHost } from "@/pages/anfragen/AnfrageDetailHost";
import { useAnfrageWizard } from "@/pages/anfragen/useAnfrageWizard";
import "@/pages/material/materialMobile.css";

function parseView(raw: string | null): AnfragenView {
  if (raw === "erledigt" || raw === "alle") return raw;
  return "offen";
}

export function AnfragenIndexPage() {
  const { openNew, openDraft, subscribeNotice, subscribeAnfragenChanged } = useAnfrageWizard();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<AnfrageListItem[]>([]);
  const [mailCounts, setMailCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
  const [view, setView] = useState<AnfragenView>(() => parseView(searchParams.get("view")));

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<AnfrageDetail | null>(null);
  const [startAction, setStartAction] = useState<"convert" | "send" | null>(null);

  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(h);
  }, [search]);

  const reloadList = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setLoading(true);
      setError(null);
      try {
        const q = debouncedSearch || undefined;
        const [list, counts] = await Promise.all([
          listAnfragen(
            view === "erledigt"
              ? { q, status: "erledigt" }
              : view === "offen"
                ? { q, open_only: true }
                : { q },
          ),
          getAnfrageMailCandidateCounts().catch(() => ({})),
        ]);
        setRows(list || []);
        setMailCounts(counts || {});
        notifyAnfragenMailCandidatesChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Liste konnte nicht geladen werden.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, view],
  );

  const reloadListRef = useRef(reloadList);
  reloadListRef.current = reloadList;

  const scheduleReloadList = useCallback(() => {
    void reloadListRef.current({ silent: true });
  }, []);

  useEffect(() => {
    void reloadList();
  }, [reloadList]);

  useEffect(
    () =>
      subscribeNotice((msg) => {
        if (msg) toast.success(msg);
      }),
    [subscribeNotice],
  );
  useEffect(
    () => subscribeAnfragenChanged(scheduleReloadList),
    [subscribeAnfragenChanged, scheduleReloadList],
  );

  useRealtime({
    scopes: ["anfragen:list"],
    domains: ["anfragen", "material_anforderungen", "orders"],
    events: ["anfragen_changed", "material_anforderungen_changed", "orders_changed"],
    onChange: scheduleReloadList,
    fallbackIntervalMs: 180_000,
  });

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const row = await getAnfrage(id);
      setDetail(row);
      return row;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Anfrage nicht gefunden.");
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetail = useCallback(
    async (id: number, action: "convert" | "send" | null = null) => {
      setStartAction(action);
      setDetailOpen(true);
      await loadDetail(id);
    },
    [loadDetail],
  );

  useEffect(() => {
    const idParam = searchParams.get("id");
    if (idParam) {
      const id = Number(idParam);
      if (Number.isFinite(id) && id > 0) {
        void openDetail(id);
        const next = new URLSearchParams(searchParams);
        next.delete("id");
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, openDetail]);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q != null && q !== search) {
      setSearch(q);
    }
  }, [searchParams, search]);

  function changeView(next: AnfragenView) {
    setView(next);
    const params = new URLSearchParams(searchParams);
    if (next === "offen") params.delete("view");
    else params.set("view", next);
    setSearchParams(params, { replace: true });
  }

  function handleOpenDraft(row: AnfrageListItem) {
    // Entwürfe liegen in derselben Tabelle – die Liste liefert alle Felder für den Wizard.
    openDraft(row as unknown as AnfrageDraftOut);
  }

  return (
    <AppShell
      title="Anfragen"
      className="material-mobile-shell material-mobile-shell--with-fab"
      subtitle="Preise oder Angebote bei Lieferanten anfragen – für ein Kundenangebot oder vor einer Bestellung. Antworten aus dem Postfach landen automatisch an der Anfrage."
      actions={
        <div className="hidden flex-wrap gap-2 sm:flex">
          <Button size="lg" data-tour="anfrage-neu" onClick={openNew}>
            + Neue Anfrage
          </Button>
        </div>
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <AnfragenListSection
        rows={rows}
        loading={loading}
        search={search}
        view={view}
        mailCounts={mailCounts}
        onSearchChange={setSearch}
        onViewChange={changeView}
        actions={{
          onOpen: (row) => void openDetail(row.id),
          onOpenDraft: handleOpenDraft,
          onSend: (row) => void openDetail(row.id, "send"),
          onNew: openNew,
        }}
      />

      <AnfrageDetailHost
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        mailCounts={mailCounts}
        startAction={startAction}
        onStartActionConsumed={() => setStartAction(null)}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
          setStartAction(null);
        }}
        onReloadDetail={() => {
          if (detail?.id) void loadDetail(detail.id);
        }}
        onChanged={() => void reloadList({ silent: true })}
      />

      <button
        type="button"
        className="material-mobile-fab sm:hidden"
        data-tour="anfrage-neu"
        onClick={openNew}
        aria-label="Neue Anfrage"
      >
        <span aria-hidden>+</span>
        <span>Neue Anfrage</span>
      </button>
    </AppShell>
  );
}
